// @ts-check

import BaseModel from "@kaspernj/api-maker/build/base-model.js"
import Ability from "./ability.js"
import AbilityGroup from "./ability-group.js"

const modelClassData = {
  "attributes": {
    "ability_id": {
      "column": {
        "default": null,
        "name": "ability_id",
        "null": false,
        "type": "uuid"
      },
      "name": "ability_id",
      "selected_by_default": null,
      "translated": null
    },
    "ability_group_id": {
      "column": {
        "default": null,
        "name": "ability_group_id",
        "null": false,
        "type": "uuid"
      },
      "name": "ability_group_id",
      "selected_by_default": null,
      "translated": null
    },
    "id": {
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": "uuid"
      },
      "name": "id",
      "selected_by_default": null,
      "translated": null
    }
  },
  "className": "AbilityGroupAbility",
  "collectionKey": "ability_group_abilities",
  "collectionName": "ability_group_abilities",
  "i18nKey": "ability_group_ability",
  "camelizedLower": "abilityGroupAbility",
  "name": "AbilityGroupAbility",
  "nameDasherized": "ability-group-ability",
  "pluralName": "ability_group_abilities",
  "ransackable_associations": [
    {
      "className": "Ability",
      "collectionName": "abilities",
      "foreignKey": "ability_id",
      "name": "ability",
      "macro": "belongs_to",
      "resource_name": "Ability",
      "through": null
    },
    {
      "className": "AbilityGroup",
      "collectionName": "ability_groups",
      "foreignKey": "ability_group_id",
      "name": "ability_group",
      "macro": "belongs_to",
      "resource_name": "AbilityGroup",
      "through": null
    },
    {
      "className": "Log",
      "collectionName": "logs",
      "foreignKey": "trackable_id",
      "name": "logs",
      "macro": "has_many",
      "resource_name": "Log",
      "through": null
    },
    {
      "className": "Tagging",
      "collectionName": "taggings",
      "foreignKey": "resource_id",
      "name": "taggings",
      "macro": "has_many",
      "resource_name": "Tagging",
      "through": null
    },
    {
      "className": "Tag",
      "collectionName": "tags",
      "foreignKey": "tag_id",
      "name": "tags",
      "macro": "has_many",
      "resource_name": "Tag",
      "through": "taggings"
    },
    {
      "className": "Tenant",
      "collectionName": "tenants",
      "foreignKey": "tenant_id",
      "name": "tenant",
      "macro": "belongs_to",
      "resource_name": "Tenant",
      "through": null
    },
    {
      "className": "WorkerPlugins::WorkplaceLink",
      "collectionName": "workplace_links",
      "foreignKey": "resource_id",
      "name": "workplace_links",
      "macro": "has_many",
      "resource_name": "WorkplaceLink",
      "through": null
    }
  ],
  "ransackable_attributes": [
    {
      "name": "ability_group_id",
      "column": {
        "default": null,
        "name": "ability_group_id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "ability_id",
      "column": {
        "default": null,
        "name": "ability_id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "created_at",
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      }
    },
    {
      "name": "id",
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "id_string",
      "column": null
    },
    {
      "name": "old_ability_group_id",
      "column": {
        "default": null,
        "name": "old_ability_group_id",
        "null": true,
        "type": "integer"
      }
    },
    {
      "name": "old_ability_id",
      "column": {
        "default": null,
        "name": "old_ability_id",
        "null": true,
        "type": "integer"
      }
    },
    {
      "name": "old_id",
      "column": {
        "default": null,
        "name": "old_id",
        "null": true,
        "type": "integer"
      }
    },
    {
      "name": "tenant_id",
      "column": {
        "default": null,
        "name": "tenant_id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "updated_at",
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      }
    }
  ],
  "ransackable_scopes": [
    {
      "name": "in_current_workplace"
    },
    {
      "name": "soft_deleted_filter"
    },
    {
      "name": "sort_by_in_current_workplace_asc"
    },
    {
      "name": "sort_by_in_current_workplace_desc"
    }
  ],
  "relationships": [
    {
      "className": "Ability",
      "collectionName": "abilities",
      "foreignKey": "ability_id",
      "name": "ability",
      "macro": "belongs_to",
      "resource_name": "Ability",
      "through": null
    },
    {
      "className": "AbilityGroup",
      "collectionName": "ability_groups",
      "foreignKey": "ability_group_id",
      "name": "ability_group",
      "macro": "belongs_to",
      "resource_name": "AbilityGroup",
      "through": null
    }
  ],
  "paramKey": "ability_group_ability",
  "primaryKey": "id"
}

/** Frontend model for AbilityGroupAbility. */
export default class AbilityGroupAbility extends BaseModel {
  /** @returns {typeof modelClassData} */
  static modelClassData() {
    return modelClassData
  }

  /** @returns {string} */
  abilityId() {
    return this.readAttributeUnderscore("ability_id")
  }

  /** @returns {boolean} */
  hasAbilityId() {
    const value = this.abilityId()

    return this._isPresent(value)
  }

  /** @returns {string} */
  abilityGroupId() {
    return this.readAttributeUnderscore("ability_group_id")
  }

  /** @returns {boolean} */
  hasAbilityGroupId() {
    const value = this.abilityGroupId()

    return this._isPresent(value)
  }

  /** @returns {string} */
  id() {
    return this.readAttributeUnderscore("id")
  }

  /** @returns {boolean} */
  hasId() {
    const value = this.id()

    return this._isPresent(value)
  }

  /** @returns {import("./ability.js").default | null} */
  ability() {
    return this._readBelongsToReflection({modelClass: Ability, reflectionName: "ability"})
  }

  /** @returns {Promise<import("./ability.js").default | null>} */
  loadAbility() {
    if (!("abilityId" in this)) throw new Error("Foreign key method wasn't defined: abilityId")

    const id = this.abilityId()
    const ransack = {}

    ransack["id_eq"] = id

    return this._loadBelongsToReflection(
      {
        reflectionName: "ability",
        model: this,
        modelClass: Ability
      },
      {ransack}
    )
  }

  /** @returns {Promise<import("./ability.js").default | null>} */
  ensureAbilityLoaded() {
    return this.ensureAssociationLoaded("ability")
  }

  /** @returns {import("./ability-group.js").default | null} */
  abilityGroup() {
    return this._readBelongsToReflection({modelClass: AbilityGroup, reflectionName: "ability_group"})
  }

  /** @returns {Promise<import("./ability-group.js").default | null>} */
  loadAbilityGroup() {
    if (!("abilityGroupId" in this)) throw new Error("Foreign key method wasn't defined: abilityGroupId")

    const id = this.abilityGroupId()
    const ransack = {}

    ransack["id_eq"] = id

    return this._loadBelongsToReflection(
      {
        reflectionName: "ability_group",
        model: this,
        modelClass: AbilityGroup
      },
      {ransack}
    )
  }

  /** @returns {Promise<import("./ability-group.js").default | null>} */
  ensureAbilityGroupLoaded() {
    return this.ensureAssociationLoaded("ability_group")
  }
}
